import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, CreditCard, Plane, Award, Car, ArrowLeft, ArrowRight, Check, Clock, Shield, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentServices() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [formData, setFormData] = useState({
    service_type: 'document_clearing',
    title: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      setFormData(prev => ({
        ...prev,
        contact_name: parsed.name || '',
        contact_email: parsed.email || ''
      }));
    }
  }, []);

  const services = [
    {
      id: 'visa_emirates',
      title: isAr ? 'تقديم طلب التأشيرة والهوية الإماراتية' : 'Apply for Visa & Emirates ID',
      icon: CreditCard,
      gradient: 'from-blue-500 to-indigo-500',
      bg: 'bg-blue-500/10',
      color: '#3B82F6',
      description: isAr 
        ? 'خدمات شاملة للتأشيرات والهوية الإماراتية - تأشيرة ذهبية، خضراء، عائلية، وأكثر'
        : 'Complete visa & Emirates ID services - Golden, Green, Family visas & more',
      includedServices: isAr 
        ? ['الإقامة الذهبية', 'الإقامة الخضراء', 'تأشيرة العائلة', 'الهوية الإماراتية']
        : ['Golden Visa', 'Green Visa', 'Family Visa', 'Emirates ID'],
      hasSubPage: true,
      link: '/services/visa'
    },
    {
      id: 'tourist_visa',
      title: isAr ? 'تأشيرة سياحية لجميع الدول والجنسيات' : 'Tourist Visa for All Countries',
      icon: Plane,
      gradient: 'from-emerald-500 to-teal-500',
      bg: 'bg-emerald-500/10',
      color: '#10B981',
      description: isAr 
        ? 'خدمات تأشيرة سياحية شاملة للسفر الدولي مع دعم لجميع الدول والجنسيات.'
        : 'Comprehensive tourist visa services for international travel.',
      includedServices: isAr 
        ? ['دعم متعدد الدول', 'تحضير المستندات', 'التنسيق مع السفارات', 'تأمين السفر']
        : ['Multi-country Support', 'Document Preparation', 'Embassy Coordination', 'Travel Insurance'],
      hasSubPage: false
    },
    {
      id: 'document_cert',
      title: isAr ? 'توثيق المستندات' : 'Document Certification',
      icon: Award,
      gradient: 'from-purple-500 to-pink-500',
      bg: 'bg-purple-500/10',
      color: '#A855F7',
      description: isAr 
        ? 'خدمات توثيق وتصديق المستندات الاحترافية للمستندات والشهادات الرسمية.'
        : 'Professional document certification and attestation services.',
      includedServices: isAr 
        ? ['تصديق الشهادات', 'خدمات الترجمة', 'توثيق المستندات', 'خدمات الأبوستيل']
        : ['Certificate Attestation', 'Translation Services', 'Document Notarization', 'Apostille Services'],
      hasSubPage: false
    },
    {
      id: 'driving_services',
      title: isAr ? 'خدمات رخصة القيادة' : 'Driving License Services',
      icon: Car,
      gradient: 'from-orange-500 to-amber-500',
      bg: 'bg-orange-500/10',
      color: '#F97316',
      description: isAr 
        ? 'إصدار وتجديد وترجمة رخص القيادة بأنواعها المختلفة.'
        : 'Issue, renew and translate driving licenses of all types.',
      includedServices: isAr 
        ? ['إصدار رخصة جديدة', 'تجديد الرخصة', 'ترجمة الرخصة', 'تحويل الرخصة']
        : ['New License', 'License Renewal', 'License Translation', 'License Conversion'],
      hasSubPage: false
    }
  ];

  const handleStartService = (service) => {
    // Allow users to view service details without login
    if (service.hasSubPage && service.link) {
      navigate(service.link);
    } else {
      setSelectedService(service);
      setFormData(prev => ({
        ...prev,
        title: service.title,
        service_type: service.id
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/services/request`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isAr ? 'تم إرسال طلبك بنجاح! سنتواصل معك قريباً' : 'Request submitted successfully!');
      setSelectedService(null);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isAr ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero Section - Compact */}
      <section className={`py-6 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-gradient-to-r from-orange-500 to-amber-500'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <button 
            onClick={() => navigate('/services')}
            className={`flex items-center gap-2 mb-4 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-white/80 hover:text-white'}`}
          >
            {isAr ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
            {isAr ? 'العودة' : 'Back'}
          </button>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-white/20'}`}>
              <FileText className={`h-8 w-8 ${isDarkMode ? 'text-amber-400' : 'text-white'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>
                {isAr ? 'الطباعة وتخليص المستندات' : 'Typing & Document Clearing'}
              </h1>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-white/80'}`}>
                {isAr ? 'خدمات سريعة وموثوقة للدوائر الحكومية' : 'Fast & reliable government services'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services or Form */}
      <section className={`py-6 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-6">
          {!selectedService ? (
            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service) => (
                <div 
                  key={service.id}
                  className={`group rounded-2xl p-6 transition-all duration-300 hover:-translate-y-2 ${isDarkMode ? 'bg-[#141414] border border-white/5 hover:border-orange-500/30 hover:shadow-lg hover:shadow-orange-500/10' : 'bg-white border-2 border-gray-200 shadow-md hover:shadow-xl hover:border-orange-500/50'}`}
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-5`}>
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  
                  <h3 className={`text-xl font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                    {service.title}
                  </h3>
                  
                  <p className={`text-sm mb-5 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {service.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {service.includedServices.map((item, idx) => (
                      <div key={idx} className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={() => handleStartService(service)}
                    className={`w-full bg-gradient-to-r ${service.gradient} text-white`}
                  >
                    {service.hasSubPage ? (isAr ? 'عرض التفاصيل' : 'View Details') : (isAr ? 'طلب الخدمة' : 'Request Service')}
                    {isAr ? <ArrowLeft className="h-4 w-4 ms-2" /> : <ArrowRight className="h-4 w-4 ms-2" />}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className={`max-w-2xl mx-auto rounded-2xl p-8 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border-2 border-gray-200 shadow-lg'}`}>
              <button 
                onClick={() => setSelectedService(null)}
                className={`flex items-center gap-2 mb-6 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-[#0f172a]'}`}
              >
                {isAr ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
                {isAr ? 'العودة للخدمات' : 'Back to Services'}
              </button>

              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${selectedService.gradient} flex items-center justify-center`}>
                  <selectedService.icon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h2 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                    {selectedService.title}
                  </h2>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {isAr ? 'يرجى ملء النموذج أدناه' : 'Please fill the form below'}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''}>
                    {isAr ? 'تفاصيل الطلب' : 'Request Details'}
                  </Label>
                  <Textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    placeholder={isAr ? 'اشرح طلبك بالتفصيل...' : 'Describe your request in detail...'}
                    className={`mt-2 ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : ''}`}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'الاسم' : 'Name'}
                    </Label>
                    <Input
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className={`mt-2 ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : ''}`}
                    />
                  </div>
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''}>
                      {isAr ? 'البريد الإلكتروني' : 'Email'}
                    </Label>
                    <Input
                      type="email"
                      required
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className={`mt-2 ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : ''}`}
                    />
                  </div>
                </div>

                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''}>
                    {isAr ? 'رقم الهاتف' : 'Phone Number'}
                  </Label>
                  <Input
                    type="tel"
                    required
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className={`mt-2 ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : ''}`}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-gradient-to-r ${selectedService.gradient} text-white py-6 text-lg`}
                >
                  {loading 
                    ? (isAr ? 'جاري الإرسال...' : 'Submitting...') 
                    : (isAr ? 'إرسال الطلب' : 'Submit Request')}
                </Button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      {!selectedService && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className={`text-3xl font-semibold text-center mb-12 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
              {isAr ? 'لماذا تختارنا؟' : 'Why Choose Us?'}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className={`text-center p-6 rounded-2xl ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border-2 border-gray-200'}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-orange-500/10' : 'bg-orange-100'}`}>
                  <Clock className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                  {isAr ? 'سرعة الإنجاز' : 'Fast Processing'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isAr ? 'إنجاز المعاملات في أسرع وقت ممكن' : 'Complete transactions as fast as possible'}
                </p>
              </div>
              <div className={`text-center p-6 rounded-2xl ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border-2 border-gray-200'}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-orange-500/10' : 'bg-orange-100'}`}>
                  <Shield className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                  {isAr ? 'موثوقية عالية' : 'High Reliability'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isAr ? 'خدمات موثوقة ومضمونة 100%' : '100% reliable and guaranteed services'}
                </p>
              </div>
              <div className={`text-center p-6 rounded-2xl ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border-2 border-gray-200'}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-orange-500/10' : 'bg-orange-100'}`}>
                  <Award className="h-8 w-8 text-orange-500" />
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                  {isAr ? 'خبرة طويلة' : 'Long Experience'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isAr ? 'سنوات من الخبرة في تخليص المعاملات' : 'Years of experience in document clearing'}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
